<template>
  <app-overlay
    :model-value="modelValue"
    :size="size"
    :return-focus="returnFocus"
    :z-class="zClass"
    :dismissible="dismissible"
    :panel-class="$attrs.class"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('close')"
  >
    <header
      v-if="title || $slots.header || showClose"
      class="flex items-start justify-between gap-4 border-b border-theme-border px-5 py-4"
    >
      <slot name="header">
        <div class="min-w-0">
          <DialogTitle
            v-if="title"
            as="h2"
            class="font-semibold leading-6 text-theme-text"
            :class="titleClass || 'text-base'"
          >
            {{ title }}
          </DialogTitle>
          <DialogDescription
            v-if="description"
            as="p"
            class="mt-1 text-sm leading-5 text-theme-text-secondary"
          >
            {{ description }}
          </DialogDescription>
        </div>
      </slot>

      <button
        v-if="showClose"
        type="button"
        class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-theme-sm text-theme-text-secondary transition hover:bg-theme-bg-subtle hover:text-theme-text"
        :aria-label="closeLabel || 'Close dialog'"
        @click="$emit('update:modelValue', false)"
      >
        <icon :path="mdiClose" size="18" />
      </button>
    </header>

    <div class="min-h-0 flex-1 overflow-y-auto px-5 py-4" :class="bodyClass">
      <slot />
    </div>

    <footer
      v-if="$slots.footer"
      class="flex flex-wrap items-center justify-end gap-2 border-t border-theme-border px-5 py-4"
    >
      <slot name="footer" />
    </footer>
  </app-overlay>
</template>

<script setup>
  /**
   * AppDialog — free-form content dialog on the shared `AppOverlay` shell.
   *
   * Use for content-panel dialogs (forms, pickers, info) that don't fit
   * AppModal's confirm/cancel footer. Same themed panel + Headless a11y as
   * AppModal; the body and footer are entirely caller-defined. Dismissible by
   * default (Esc / backdrop) via AppOverlay.
   */
  import Icon from '@/components/Icon';
  import AppOverlay from '@/components/ui/overlays/AppOverlay.vue';
  import { DialogDescription, DialogTitle } from '@headlessui/vue';
  import { mdiClose } from '@mdi/js';

  defineOptions({ name: 'AppDialog', inheritAttrs: false });

  defineProps({
    returnFocus: { type: Function, default: null },
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    titleClass: { type: String, default: '' },
    description: { type: String, default: '' },
    bodyClass: { type: [String, Array, Object], default: '' },
    showClose: { type: Boolean, default: true },
    closeLabel: { type: String, default: '' },
    size: { type: String, default: 'md' },
    /** When false, Escape / backdrop no longer dismiss the dialog. */
    dismissible: { type: Boolean, default: true },
    /** Stacking context — raise above another open dialog when nested. */
    zClass: { type: String, default: 'z-dialog' },
  });

  defineEmits(['update:modelValue', 'close']);
</script>
